import React, { useEffect, useState, useRef } from "react";
import io from "socket.io-client";
import Peer from "simple-peer";
import styled from "styled-components";
import Draggable from "react-draggable";
import { IconContext } from "react-icons";
import { FaPhoneAlt, FaComments } from "react-icons/fa";
import { RiMic2Line } from "react-icons/ri";
import { MdCamera } from "react-icons/md";
import { getDatabase, ref, push, get, set, update } from "firebase/database";
import "./VideoChat.css";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div``;

const Video = styled.video`
  position: relative;
`;

const VideoChat = (props) => {
  const [yourID, setYourID] = useState("");
  const [members, setMembers] = useState([]);
  const [stream, setStream] = useState();
  const [receivingCall, setReceivingCall] = useState(false);
  const [caller, setCaller] = useState("");
  const [callerSignal, setCallerSignal] = useState();
  const [callAccepted, setCallAccepted] = useState(false);
  const [toggleVid, setToggle] = useState(true);
  const [toggleAud, setAudToggle] = useState(true);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const userVideo = useRef();
  const partnerVideo = useRef();
  const socket = useRef();
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef([]);
  const isPlayingRef = useRef(false);
  const isConnected = useRef(false);
  const sessionJoined = useRef(false);
  const peer = useRef();
  const pendingSignal = useRef(null);
  const hasProcessedSignal = useRef(false);
  const isCaller = useRef(false);
  
  useEffect(() => {
    // Initialize audio context
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();

    const baseURL = process.env.NODE_ENV === "production"
      ? "https://interview-sandbox-production.up.railway.app/"
      : "http://localhost:8000/";

    if (!socket.current) {
      // console.log("Creating new socket connection...");
      socket.current = io(process.env.NODE_ENV === 'production' 
        ? 'https://interview-sandbox-production.up.railway.app' 
        : 'http://localhost:8000', {
        transports: ['websocket', 'polling'],
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        forceNew: true,
        path: '/socket.io/',
        autoConnect: false
      });
      
      socket.current.on('connect', () => {
        // console.log('Socket connected successfully');
        isConnected.current = true;
        if (props.sessionid) {
          // console.log(`Attempting to join session ${props.sessionid}...`);
          socket.current.emit("join-session", {
            sessionid: props.sessionid,
            threadID: props.threadID,
          });
        }
      });

      socket.current.on('joined-session', (data) => {
        // console.log('Successfully joined session:', data);
        sessionJoined.current = true;
      });

      socket.current.on('session-error', (data) => {
        console.error('Session error:', data.message);
      });

      socket.current.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        isConnected.current = false;
      });

      socket.current.on('disconnect', (reason) => {
        // console.log('Socket disconnected:', reason);
        isConnected.current = false;
        sessionJoined.current = false;
        // Clean up peer connection on disconnect
        if (peer.current) {
          peer.current.destroy();
          peer.current = null;
        }
      });

      // User events
      socket.current.on("yourID", (id) => {
        // console.log('Received yourID:', id);
        setYourID(id);
      });

      socket.current.on("allUsers", (members) => {
        // console.log('Received members list:', members);
        setMembers(members);
      });

      socket.current.on("user-joined", (data) => {
        // console.log('User joined:', data);
        setMembers(data.allMembers);
      });

      socket.current.on("user-left", (data) => {
        console.log('User left:', data);
        setMembers(data.remainingMembers);
      });

      // Call events
      socket.current.on("hey", (data) => {
        // console.log('Incoming call from:', data.from);
        setReceivingCall(true);
        setCaller(data.from);
        setCallerSignal(data.signal);
      });

      socket.current.on("callAccepted", (signal) => {
        // console.log('Call accepted with signal:', signal);
        if (hasProcessedSignal.current) {
          console.log('Signal already processed, ignoring duplicate');
          return;
        }

        if (peer.current) {
          try {
            peer.current.signal(signal);
            hasProcessedSignal.current = true;
          } catch (error) {
            console.error('Error signaling peer:', error);
          }
        } else {
          console.log('Storing signal for later use');
          pendingSignal.current = signal;
        }
      });

      // Assistant events
      socket.current.on("assistant-response", (response) => {
        if (props.onAssistantUpdate) {
          props.onAssistantUpdate(response, false);
        }
      });

      socket.current.on("response-complete", () => {
        if (props.onAssistantUpdate) {
          props.onAssistantUpdate("", true);
        }
      });

      socket.current.on("tts-audio", async (chunk) => {
        try {
          let arrayBuffer;
          if (chunk instanceof ArrayBuffer) {
            arrayBuffer = chunk;
          } else if (chunk instanceof Blob) {
            arrayBuffer = await chunk.arrayBuffer();
          } else if (typeof chunk === "string") {
            arrayBuffer = new TextEncoder().encode(chunk).buffer;
          } else {
            throw new Error("Received chunk is neither ArrayBuffer nor Blob");
          }

          audioBufferRef.current.push(arrayBuffer);
          if (!isPlayingRef.current) {
            isPlayingRef.current = true;
            playAudio();
          }
        } catch (error) {
          console.error("Error processing audio chunk:", error);
        }
      });
    }

    // Connect the socket
    socket.current.connect();

    // Cleanup function
    return () => {
      if (socket.current) {
        // console.log('Cleaning up socket connection');
        socket.current.disconnect();
        socket.current = null; // Ensure socket ref is cleared
        isConnected.current = false;
        sessionJoined.current = false;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      // Stop media stream tracks
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (peer.current) {
        peer.current.destroy();
        peer.current = null;
      }
      hasProcessedSignal.current = false;
      pendingSignal.current = null;
      isCaller.current = false;
    };
    // Dependencies: only props.sessionid and props.threadID should trigger re-runs
  }, [props.sessionid, props.threadID]);

  const saveMessageToFirebase = async (type, content) => {
    // Check if the content is empty or only whitespace
    if (!content || content.trim() === '') {
      return; // Don't save empty messages
    }

    const db = getDatabase();
    const conversationRef = ref(db, `code-sessions/${props.sessionid}/conversation`);
    
    // Get the current conversation
    const snapshot = await get(conversationRef);
    const conversation = snapshot.val() || {};
    
    // Find the next available index
    const nextIndex = Object.keys(conversation).length;
    
    // Add the new message
    const newMessage = {
      type,
      content: content.trim(), // Trim any leading/trailing whitespace
      timestamp: Date.now()
    };
    
    // Update the conversation in Firebase
    await update(conversationRef, {
      [nextIndex]: newMessage
    });
  };

  const saveAIResponseToFirebase = (response) => {
    const db = getDatabase();
    const sessionRef = ref(db, `code-sessions/${props.sessionid}/conversation`);
    push(sessionRef, {
      type: 'ai',
      content: response,
      timestamp: Date.now()
    });
  };

  // Modify the updateAssistant function to save AI responses to Firebase
  const updateAssistant = (response, isComplete) => {
    if (props.onAssistantUpdate) {
      props.onAssistantUpdate(response, isComplete);
      // Save AI response to Firebase only if it's not empty
      if (response && response.trim() !== '') {
        saveMessageToFirebase('ai', response);
      }
    }
  };

  const playAudio = async () => {
    if (isPlayingRef.current || audioBufferRef.current.length === 0) return;

    isPlayingRef.current = true;

    const playNextChunk = async () => {
      if (audioBufferRef.current.length === 0) {
        isPlayingRef.current = false;
        return;
      }

      const chunk = audioBufferRef.current.shift();
      try {
        const audioBuffer = await audioContextRef.current.decodeAudioData(chunk);
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContextRef.current.destination);
        
        source.onended = playNextChunk;
        source.start();
      } catch (error) {
        console.error("Error decoding audio data:", error);
        playNextChunk(); // Move to the next chunk if there's an error
      }
    };

    playNextChunk();
  };

  const concatArrayBuffers = (buffers) => {
    const totalLength = buffers.reduce((acc, buffer) => acc + buffer.byteLength, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;

    for (const buffer of buffers) {
      result.set(new Uint8Array(buffer), offset);
      offset += buffer.byteLength;
    }

    return result.buffer;
  };

  function toggleTrack(trackIndex, currentToggleValue, setToggleFunction) {
    if (!stream) return;

    const trackEnabled = stream.getTracks()[trackIndex].enabled;
    const newToggleValue = !trackEnabled;

    stream.getTracks()[trackIndex].enabled = newToggleValue;
    setToggleFunction(newToggleValue);
  }

  async function toggleVideo(constraints) {
    if (stream) {
      toggleTrack(1, toggleVid, setToggle);
    }
  }

  async function toggleAudio(constraints) {
    if (stream) {
      toggleTrack(0, toggleAud, setAudToggle);
    }
  }

  async function callPeer(id) {
    if (!socket.current || !isConnected.current) {
      console.error('Cannot call: socket not connected');
      return;
    }

    try {
      // Request camera and microphone permissions when initiating a call
      const currentStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setStream(currentStream);
      if (userVideo.current) {
        userVideo.current.srcObject = currentStream;
      }

      console.log('Initiating call to', id);
      hasProcessedSignal.current = false;
      isCaller.current = true;
      setCallAccepted(true);
      
      const newPeer = new Peer({
        initiator: true,
        trickle: false,
        stream: currentStream,
        config: {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' }
          ]
        }
      });

      newPeer.on("signal", (data) => {
        console.log('Sending signal data:', data);
        socket.current.emit("callUser", {
          userToCall: id,
          signalData: data,
          from: yourID,
        });
      });

      newPeer.on("stream", (stream) => {
        console.log('Received remote stream in caller:', stream);
        if (stream) {
          console.log('Remote stream tracks:', stream.getTracks().length);
          setRemoteStream(stream);
        } else {
          console.error('Received null stream in caller');
        }
      });

      newPeer.on("error", (err) => {
        console.error('Peer error in caller:', err);
      });

      newPeer.on("connect", () => {
        console.log('Peer connection established in caller');
      });

      newPeer.on("close", () => {
        console.log('Peer connection closed in caller');
        setRemoteStream(null);
        setCallAccepted(false);
        // Stop local stream when call ends
        if (currentStream) {
          currentStream.getTracks().forEach(track => track.stop());
        }
        setStream(null);
      });

      peer.current = newPeer;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      alert('Unable to access camera and microphone. Please ensure you have granted the necessary permissions.');
    }
  }

  async function acceptCall() {
    if (!socket.current || !isConnected.current) {
      console.error('Cannot accept call: socket not connected');
      return;
    }

    try {
      // Request camera and microphone permissions when accepting a call
      const currentStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setStream(currentStream);
      if (userVideo.current) {
        userVideo.current.srcObject = currentStream;
      }

      setCallAccepted(true);
      hasProcessedSignal.current = false;
      isCaller.current = false;
      
      const newPeer = new Peer({
        initiator: false,
        trickle: false,
        stream: currentStream,
        config: {
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' }
          ]
        }
      });

      newPeer.on("signal", (data) => {
        console.log('Sending signal data:', data);
        socket.current.emit("acceptCall", { signal: data, to: caller });
      });

      newPeer.on("stream", (stream) => {
        console.log('Received remote stream in callee:', stream);
        if (stream) {
          console.log('Remote stream tracks:', stream.getTracks().length);
          setRemoteStream(stream);
        } else {
          console.error('Received null stream in callee');
        }
      });

      newPeer.on("error", (err) => {
        console.error('Peer error in callee:', err);
      });

      newPeer.on("connect", () => {
        console.log('Peer connection established in callee');
      });

      newPeer.on("close", () => {
        console.log('Peer connection closed in callee');
        setRemoteStream(null);
        // Stop local stream when call ends
        if (currentStream) {
          currentStream.getTracks().forEach(track => track.stop());
        }
        setStream(null);
      });

      if (callerSignal) {
        newPeer.signal(callerSignal);
      } else {
        console.error('No caller signal available');
      }

      peer.current = newPeer;

      if (pendingSignal.current) {
        newPeer.signal(pendingSignal.current);
        pendingSignal.current = null;
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      alert('Unable to access camera and microphone. Please ensure you have granted the necessary permissions.');
    }
  }

  // Handle local video stream
  useEffect(() => {
    if (stream && userVideo.current) {
      // console.log('Setting local video source');
      try {
        userVideo.current.srcObject = stream;
        userVideo.current.onloadedmetadata = () => {
          // console.log('Local video metadata loaded');
          userVideo.current.play().catch(err => {
            console.error('Error playing local video:', err);
          });
        };
      } catch (error) {
        console.error('Error setting local video source:', error);
      }
    }
  }, [stream]);

  // Handle remote video stream
  useEffect(() => {
    // console.log('Remote stream effect triggered, remoteStream:', remoteStream);
    if (remoteStream && partnerVideo.current) {
      console.log('Setting remote video source');
      try {
        partnerVideo.current.srcObject = remoteStream;
        partnerVideo.current.onloadedmetadata = () => {
          console.log('Remote video metadata loaded');
          partnerVideo.current.play().catch(err => {
            console.error('Error playing remote video:', err);
          });
        };
      } catch (error) {
        console.error('Error setting remote video source:', error);
      }
    } else {
      // console.log('Remote video not ready:', {
      //   hasRemoteStream: !!remoteStream,
      //   hasPartnerVideo: !!partnerVideo.current
      // });
    }
  }, [remoteStream]);

  let UserVideo;
  if (stream) {
    UserVideo = (
      <Draggable nodeRef={userVideo}>
        <Video
          className={props.whiteboardExtend === false ? "videoAdjust" : "videoDiv-ext"}
          playsInline
          muted
          ref={userVideo}
          autoPlay
        />
      </Draggable>
    );
  }

  let PartnerVideo;
  if (callAccepted && remoteStream) {
    console.log('Rendering partner video with stream:', remoteStream.id);
    PartnerVideo = (
      <Draggable nodeRef={partnerVideo}>
        <Video
          className={props.whiteboardExtend === false ? "videoAdjust" : "videoDiv-ext"}
          playsInline
          ref={partnerVideo}
          autoPlay
        />
      </Draggable>
    );
  } else {
    // console.log('Partner video not rendered:', {
    //   callAccepted,
    //   hasRemoteStream: !!remoteStream
    // });
  }

  const CallButton = () => {
    const buttonClass = callerSignal === undefined ? "callBtn" : "callBtn-pulse";

    return (
      <div onClick={acceptCall} className={buttonClass}>
        <CallIcon />
      </div>
    );
  };

  const CallIcon = () => {
    const iconContextValue = {
      size: "1.4em",
      style: { marginRight: "0.2rem", marginTop: "0.1rem" },
    };

    return (
      <IconContext.Provider value={iconContextValue}>
        <FaComments />
      </IconContext.Provider>
    );
  };

  let incomingCall = null;

  if (receivingCall && !callAccepted) {
    incomingCall = (
      <div>
        <CallButton />
      </div>
    );
  }

  // User interaction to initialize audio context
  useEffect(() => {
    const initAudioContext = async () => {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
    };

    const handleUserInteraction = () => {
      initAudioContext();
      document.removeEventListener('click', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
    };
  }, []);

  return (
    <Container className="videoContainer">
      <Row className="buttonsRow">
        {callAccepted && (
          <>
            <div
              title="camera"
              className={toggleVid === true ? "toggleVid-on" : "toggleVid-off"}
              onClick={() => toggleVideo()}
            >
              <IconContext.Provider
                value={{ size: "1.4em", style: { marginRight: "0.2rem", marginTop: "0.1rem" } }}
              >
                <MdCamera />
              </IconContext.Provider>
            </div>
            <div
              title="microphone"
              className={toggleAud === true ? "toggleAud-on" : "toggleAud-off"}
              onClick={() => toggleAudio()}
            >
              <IconContext.Provider
                value={{ size: "1.4em", style: { marginRight: "0.2rem", marginTop: "0.1rem" } }}
              >
                <RiMic2Line />
              </IconContext.Provider>
            </div>
          </>
        )}
        {callAccepted === false &&
          yourID &&
          members.length > 0 &&
          members
            .filter((memberName) => memberName !== yourID)
            .map((memberName) => (
              <div
                key={memberName}
                className="toggleVid-on"
                onClick={() => callPeer(memberName)}
              >
                <IconContext.Provider
                  value={{
                    size: "1.4em",
                    style: { marginRight: "0.2rem", marginTop: "0.1rem" },
                  }}
                >
                  <FaPhoneAlt />
                </IconContext.Provider>
              </div>
            ))}
        <div>{incomingCall}</div>
      </Row>
      <div className={props.whiteboardExtend === false ? "videoDiv" : "videoDiv-ext"}>
        {UserVideo}
        {PartnerVideo}
      </div>
    </Container>
  );
};

export default VideoChat;
